import {
  dimensionLabels,
  normalizeEntityId,
  otherDimension,
} from "./e-charts-dimensions";

// Pure view model for the E data charts under R09 item 5. Every decision the
// drill feature makes about what to render lives here so it can be exercised
// without a DOM: which entities are on the chart, which of the five empty-state
// causes is in play, what the four chart titles say, and whether the
// company-wide cards belong on screen. The components below stay a thin
// mapping from these values to markup.
//
// Copy is returned as { key, params } pairs. The key doubles as its own
// English fallback, matching the rest of this feature (brief 3.2 item 15: new
// strings are hardcoded English, locale catalogs remain the platform-wide P3).

export const EMPTY_CAUSE = {
  NO_YEARS: "no-years",
  NO_ENTITIES_SELECTED: "no-entities-selected",
  OVER_LIMIT: "over-limit",
  LOAD_ERROR: "load-error",
  NOT_LOADED: "not-loaded",
  NO_OPTIONS: "no-options",
  INVALID_SELECTION: "invalid-selection",
  NO_SCOPE: "no-scope",
  FRAMEWORK: "framework",
  STRUCTURAL: "structural",
  DATA_EMPTY: "data-empty",
};

const FRAMEWORK_WARNING_CODES = new Set([
  "UNSUPPORTED_FRAMEWORK",
  "UNKNOWN_FRAMEWORK",
]);

const copy = (key, params) => (params ? { key, params } : { key });

/**
 * Entities to draw, in bar order.
 *
 * Cross-filtering makes the API OMIT any entity whose authorized intersection
 * is empty, at a narrowed top level as well as inside a drill. Coercing a
 * missing row to zero, which the hero, grid, and legend all do, would resurrect
 * those omitted entities as zero-height bars and a "0" tooltip line. So
 * whenever the request carried `within`, the displayed list takes its ORDER
 * from the option list and its MEMBERSHIP from the response rows. Only an
 * un-narrowed top level derives purely from picker state, exactly as before
 * this feature (brief 3.2 item 8).
 */
export const deriveDisplayedEntities = ({
  dimension,
  selection,
  optionIds = [],
  rows = [],
  withinSent = false,
}) => {
  // Explicit none draws nothing, on BOTH branches: the ?? full-list
  // fallbacks below would otherwise render every entity for the one mode
  // that means the opposite (Brief D R4).
  if (selection?.mode === "none") return [];

  const selectedIds =
    selection?.mode === "subset" ? (selection.ids ?? []).map(String) : null;
  const allOptionIds = (optionIds ?? []).map(String);

  if (!withinSent) return selectedIds ?? allOptionIds;

  const selectedSet = selectedIds ? new Set(selectedIds) : null;
  const rowIds = new Set((rows ?? []).map((row) => String(row?.entityId)));
  return allOptionIds
    .filter((id) => (selectedSet ? selectedSet.has(id) : true))
    .filter((id) => rowIds.has(normalizeEntityId(dimension, id)));
};

export const unknownWithinEntityIds = (warnings = []) => [
  ...new Set(
    (warnings ?? [])
      .filter((warning) => warning?.code === "UNKNOWN_WITHIN_ENTITY")
      .flatMap((warning) => warning.unknownEntityIds ?? [])
      .map(String),
  ),
];

// True only when EVERY entity the request named as `within` came back unknown.
// A partial case is a live chart with some selections silently dropped, which
// is a warning, not an empty state (brief 3.2 items 12a and 13).
export const unknownWithinCoversRequest = (warnings, requestedWithin) => {
  const requested = (requestedWithin?.entityIds ?? []).map(String);
  if (requested.length === 0) return false;
  const unknown = new Set(unknownWithinEntityIds(warnings));
  if (unknown.size === 0) return false;
  return requested.every((id) => unknown.has(id));
};

/**
 * Whether the panel renders the ambient context ("live") or keeps the
 * previous view mounted, dimmed, while its replacement is fetched
 * ("refreshing").
 *
 * Refreshing requires previous data (a snapshot of the context it answered)
 * AND a genuinely pending refetch. Every terminal condition of the ambient
 * context renders live instead, because no payload is coming to end the
 * refresh: no years selected, a selection over the API bound, a failed load,
 * and a settled-but-empty vocabulary (the ops layer refuses or fails all
 * four, and the first three are diagnosed before the staleness gate in
 * resolveEmptyState in exactly this order). Rendering live with stale data is
 * safe there: resolveEmptyState never lets stale rows draw (Codex round 3
 * finding 1), so the worst case is the explanatory state replacing the old
 * view, at the uniform height.
 */
export const resolveRenderMode = ({
  liveYearCount = 0,
  noneSelectedDimension = null,
  overLimitDimension = null,
  loadError = false,
  vocabularySettled = false,
  liveOptionCount = 0,
  isStale = false,
  hasLoadedOnce = false,
  hasSnapshot = false,
}) => {
  if (liveYearCount === 0) return "live";
  // A none selection is a terminal ambient state: the ops layer refuses the
  // fetch, so no payload is coming to end a veil (Brief D R3).
  if (noneSelectedDimension) return "live";
  if (overLimitDimension) return "live";
  if (loadError) return "live";
  if (vocabularySettled && liveOptionCount === 0) return "live";
  if (isStale && hasLoadedOnce && hasSnapshot) return "refreshing";
  return "live";
};

/**
 * Which of the empty-state causes applies, or null when the chart should draw.
 *
 * `rows.length === 0` alone is not a sufficient test: entities that have orgs
 * but no consolidation result still emit a row with null year values, and an
 * empty response now has several causes that must not share one message. The
 * order below is the brief's detection order (3.2 item 12) with the local
 * no-years and pre-load conditions folded in.
 *
 * `hasAnyValue` is a callback so the caller keeps the reader it already has;
 * it must report true for a finite zero, which IS data.
 */
export const resolveEmptyState = ({
  years = [],
  entities = [],
  optionCount = 0,
  rows = [],
  warnings = [],
  requestedWithin = null,
  hasLoadedOnce = false,
  hasAnyValue = () => false,
  noneSelectedDimension = null,
  overLimitDimension = null,
  loadError = false,
  isStale = false,
  vocabularySettled = false,
}) => {
  if (years.length === 0) return { cause: EMPTY_CAUSE.NO_YEARS };

  // An empty vocabulary with the org tree ALREADY LOADED can never fetch
  // (the ops layer refuses an empty enumeration), so it must be diagnosed
  // BEFORE the gates below or the loading sentence shows forever (e.g. a
  // tenant whose organizations carry no country tags, viewed or drilled
  // into by country). It also outranks the none-selection cause: with no
  // options, "select at least one" is advice the picker cannot satisfy.
  // While the tree is still on its way the loading gate is the honest
  // answer.
  if (vocabularySettled && optionCount === 0) {
    return { cause: EMPTY_CAUSE.NO_OPTIONS };
  }

  // An explicit none selection (Brief D R1/R2): the ops preflight refused
  // the fetch before building a body, so no payload is coming; diagnosed
  // ahead of the over-limit, error, and loading gates, which would each
  // tell the wrong story. The binding payload shape is { cause, dimension },
  // naming the empty dimension(s) from the SELECTIONS, never from the
  // displayed dimension.
  if (noneSelectedDimension) {
    return {
      cause: EMPTY_CAUSE.NO_ENTITIES_SELECTED,
      dimension: noneSelectedDimension,
    };
  }

  // The request was refused before sending: it would breach the API's
  // per-list bound and come back 400 (Codex round 3 finding 3).
  if (overLimitDimension) {
    return { cause: EMPTY_CAUSE.OVER_LIMIT, overLimitDimension };
  }

  // The matching request failed and nothing newer is scheduled. Whatever data
  // is stored answers an older context, so an explicit retry beats a chart.
  if (loadError) return { cause: EMPTY_CAUSE.LOAD_ERROR };

  // Stored data answers a different context than the one on screen (the
  // selection or drill moved and the refetch is still pending). Presenting it
  // as the current filtered result shows wrong totals under fresh headings,
  // so this check must sit BEFORE the has-data shortcut (Codex round 3
  // finding 1). A pre-first-load store is the same story.
  if (isStale || !hasLoadedOnce) return { cause: EMPTY_CAUSE.NOT_LOADED };

  const hasData =
    rows.length > 0 && entities.length > 0 && Boolean(hasAnyValue());
  if (hasData) return null;

  if (unknownWithinCoversRequest(warnings, requestedWithin)) {
    return { cause: EMPTY_CAUSE.INVALID_SELECTION };
  }
  if (warnings.some((warning) => warning?.code === "EMPTY_SCOPE")) {
    return { cause: EMPTY_CAUSE.NO_SCOPE };
  }
  // Gated on rows being empty: the chosen-framework-unusable early return is
  // the only path that produces framework warnings WITH an empty row set. A
  // framework warning about a secondary ignored framework alongside a
  // data-empty or structural condition must not misdiagnose as "pick a
  // different framework".
  if (
    rows.length === 0 &&
    warnings.some((warning) => FRAMEWORK_WARNING_CODES.has(warning?.code))
  ) {
    return { cause: EMPTY_CAUSE.FRAMEWORK };
  }
  if (optionCount === 0) return { cause: EMPTY_CAUSE.NO_OPTIONS };
  if (rows.length === 0 || entities.length === 0) {
    // No `within` means nothing was cross-filtered away, so an empty result is
    // about the data, not about the shape of the organization.
    return requestedWithin
      ? { cause: EMPTY_CAUSE.STRUCTURAL }
      : { cause: EMPTY_CAUSE.DATA_EMPTY };
  }
  return { cause: EMPTY_CAUSE.DATA_EMPTY };
};

/**
 * Sentence and inline way out for an empty state. Each cause gets its OWN
 * wording: collapsing them would tell a user to change the year when the real
 * problem is a stale filter, or blame the drill for data the framework
 * selection hid (brief 3.2 item 12, risk item 2).
 */
export const buildEmptyStateCopy = ({
  cause,
  displayedDimension,
  drillContext = null,
  overLimitDimension = null,
  noneSelectedDimension = null,
}) => {
  const displayed = dimensionLabels(displayedDimension);
  const withinDimension = otherDimension(displayedDimension);
  const within = dimensionLabels(withinDimension);
  const drillLabels = drillContext
    ? dimensionLabels(drillContext.dimension)
    : null;
  const exitAction = drillContext
    ? {
        kind: "exit-drill",
        label: copy(`Back to all ${drillLabels.many}`),
      }
    : null;

  switch (cause) {
    case EMPTY_CAUSE.NO_YEARS:
      return { message: copy("Pick at least one year."), action: null };

    case EMPTY_CAUSE.NO_ENTITIES_SELECTED: {
      // Keyed off the cause payload (which selection is empty), never off
      // the displayed dimension: in the R2 scenario the empty picker is the
      // OTHER dimension. Mid-drill the exit action rides along so the
      // breadcrumb is not the only way out.
      const message =
        noneSelectedDimension === "both"
          ? copy("Select at least one business unit and country.")
          : noneSelectedDimension === "country"
            ? copy("Select at least one country.")
            : copy("Select at least one business unit.");
      return { message, action: exitAction };
    }

    case EMPTY_CAUSE.OVER_LIMIT: {
      const limited = dimensionLabels(overLimitDimension || displayedDimension);
      return {
        message: copy(
          `The charts can show up to 100 ${limited.many} at once. Narrow the ${limited.heading} selection to continue.`,
        ),
        action: null,
      };
    }

    case EMPTY_CAUSE.LOAD_ERROR:
      return {
        message: copy("The emission data could not be loaded."),
        action: { kind: "retry", label: copy("Try again") },
      };

    case EMPTY_CAUSE.NOT_LOADED:
      return { message: copy("Loading emission data..."), action: null };

    case EMPTY_CAUSE.NO_OPTIONS:
      // Reachable mid-drill (drilling flips the displayed dimension to one
      // that may have no vocabulary), so the drill exit rides along when one
      // exists.
      return {
        message: copy(`No ${displayed.many} are available for your account.`),
        action: exitAction,
      };

    case EMPTY_CAUSE.INVALID_SELECTION:
      return drillContext
        ? {
            message: copy(
              "{{name}} is no longer available. Go back to pick another one.",
              { name: drillContext.entityName },
            ),
            action: exitAction,
          }
        : {
            message: copy(
              `The selected ${within.many} are no longer available. Update the ${within.heading} filter to continue.`,
            ),
            action: null,
          };

    case EMPTY_CAUSE.NO_SCOPE:
      return {
        message: copy(
          "No emission data is authorized for your account under the current filters.",
        ),
        action: exitAction,
      };

    case EMPTY_CAUSE.FRAMEWORK:
      // Deliberately says nothing about the drill or the pickers: the data is
      // hidden by framework narrowing, and naming the drill here would send
      // the user back out of a view that was never the problem.
      return {
        message: copy(
          "The active framework reports no scope breakdown for these years. Pick a different framework in the filter bar.",
        ),
        action: null,
      };

    case EMPTY_CAUSE.STRUCTURAL:
      if (drillContext) {
        return {
          message:
            drillContext.dimension === "country"
              ? copy(
                  `None of your selected ${displayed.many} operate in {{name}}.`,
                  { name: drillContext.entityName },
                )
              : copy(
                  `{{name}} does not operate in any of your selected ${displayed.many}.`,
                  { name: drillContext.entityName },
                ),
          action: exitAction,
        };
      }
      return {
        message: copy(
          `None of your selected ${displayed.many} match the selected ${within.many}. Widen the ${within.heading} filter or select All.`,
        ),
        action: null,
      };

    case EMPTY_CAUSE.DATA_EMPTY:
    default:
      return {
        message: copy(
          "No emission data was reported for the selected years. Try a different year range.",
        ),
        action: exitAction,
      };
  }
};

/**
 * Interaction gates for the drill.
 *
 * `canDrill` is the single source for the hero's click handler, the pointer
 * cursor, the tooltip hint line, and the visually-hidden drill controls, so
 * the affordance and the behavior cannot disagree: consolidated mode has no
 * per-entity bars, and an open drill is already at the one and only level.
 *
 * `pausedDimension` is the CLICKED dimension, whose picker is disabled so the
 * selection breadcrumb-back promises to restore cannot be edited out from
 * under the return trip. `liveDimension` keeps re-filtering the drilled view
 * in place (brief 3.2 item 10).
 */
export const resolveInteractionState = ({
  consolidated = false,
  drillContext = null,
} = {}) => ({
  canDrill: !consolidated && !drillContext,
  pausedDimension: drillContext ? drillContext.dimension : null,
  liveDimension: drillContext ? otherDimension(drillContext.dimension) : null,
});

export const buildHeroTitleCopy = ({
  displayedDimension,
  drillContext = null,
  consolidated = false,
}) => {
  if (consolidated) return copy("Total Emissions (consolidated)");
  const labels = dimensionLabels(displayedDimension);
  return drillContext
    ? copy(`Total Emissions by ${labels.one} within {{name}}`, {
        name: drillContext.entityName,
      })
    : copy(`Total Emissions by ${labels.one}`);
};

export const buildHeroDescriptionCopy = ({
  displayedDimension,
  drillContext = null,
  consolidated = false,
  entityCount = 0,
}) => {
  const labels = dimensionLabels(displayedDimension);
  if (consolidated) {
    return copy(`Sum of {{n}} selected ${labels.many}`, { n: entityCount });
  }
  return drillContext
    ? copy(`${labels.manyCapitalized} within {{name}}`, {
        name: drillContext.entityName,
      })
    : copy(
        "Each bar is one entity's total of Scope 1, 2 and 3; the charts below break it down by scope",
      );
};

export const buildScopeDescriptionCopy = ({
  displayedDimension,
  drillContext = null,
  consolidated = false,
  entityCount = 0,
}) => {
  const labels = dimensionLabels(displayedDimension);
  if (consolidated) {
    return copy(`Consolidated across {{n}} selected ${labels.many}`, {
      n: entityCount,
    });
  }
  return drillContext
    ? copy(`${labels.manyCapitalized} within {{name}}`, {
        name: drillContext.entityName,
      })
    : copy("Per entity over selected years");
};

// Back leads to the view the drilled entity was clicked in, which is grouped
// by the CLICKED dimension, not by the one now on the bars.
export const buildBreadcrumbCopy = ({ drillContext }) => {
  if (!drillContext) return null;
  const labels = dimensionLabels(drillContext.dimension);
  return {
    backLabel: copy(`All ${labels.many}`),
    currentLabel: drillContext.entityName,
  };
};

export const buildPausedPickerHintCopy = ({ drillContext }) =>
  drillContext
    ? copy("Viewing inside {{name}}; go back to change this selection", {
        name: drillContext.entityName,
      })
    : null;

export const DRILL_HINT_COPY = copy("Click to drill in");

export const buildDrillControlCopy = (entityName) =>
  copy("Drill into {{name}}", { name: entityName });

// One warning line naming the selections the API ignored, so a partial
// unknown-within never leaves the user silently looking at fewer entities than
// they picked. Suppressed by the caller only when an empty state is already
// explaining the same thing (brief 3.2 item 13).
export const buildUnknownWithinWarningCopy = ({
  withinDimension,
  unknownIds = [],
  nameById,
}) => {
  if (unknownIds.length === 0) return null;
  const labels = dimensionLabels(withinDimension);
  const names = unknownIds.map((id) => nameById?.get(String(id)) ?? String(id));
  return copy(
    `Some selected ${labels.many} no longer match any organization and were ignored: {{names}}.`,
    { names: names.join(", ") },
  );
};
